import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '../IconButton/IconButton';
import ActionMenuItems from '../Menu/ActionMenuItems';
import useActionMenuStyles from '../Menu/useActionMenuStyles';
import { formatUnreadMessagesLabel } from './rowActionsMenuUtils';

export default function RowActionsMenu({
  actions = [],
  ariaLabel = 'Row actions',
  unreadCount = 0,
  hasNewMessage = false,
}) {
  const styles = useActionMenuStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showChatNotification, setShowChatNotification] = useState(false);
  const open = Boolean(anchorEl);
  const count = Number(unreadCount) > 0 ? Number(unreadCount) : (hasNewMessage ? 1 : 0);

  const handleOpen = useCallback((event) => {
    event.stopPropagation();
    if (count > 0) setShowChatNotification(true);
    setAnchorEl(event.currentTarget);
  }, [count]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setShowChatNotification(false);
  }, []);

  const handleSelect = useCallback(
    (action) => {
      handleClose();
      action.onClick?.();
    },
    [handleClose]
  );

  const visibleActions = useMemo(
    () => actions.filter((action) => action.visible !== false),
    [actions]
  );

  const firstDestructiveIndex = useMemo(
    () => visibleActions.findIndex((action) => action.destructive),
    [visibleActions]
  );

  if (!visibleActions.length) {
    return null;
  }

  const buttonLabel = formatUnreadMessagesLabel(count, ariaLabel);

  return (
    <>
      <IconButton
        aria-label={buttonLabel}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        size="sm"
        variant="ghost"
        sx={open ? styles.triggerOpen : styles.triggerIdle}
      >
        <Badge
          badgeContent={count}
          color="error"
          max={99}
          invisible={count <= 0}
          overlap="circular"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: styles.paper,
          },
          list: {
            dense: true,
            sx: styles.list,
          },
        }}
      >
        <ActionMenuItems
          actions={visibleActions}
          onSelect={handleSelect}
          firstDestructiveIndex={firstDestructiveIndex}
          highlightedActionId={showChatNotification ? 'chat' : undefined}
          badgeCount={showChatNotification ? count : 0}
        />
      </Menu>
    </>
  );
}
